import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGIN } from "@/constants/testIds";
import { AppFooter } from "@/components/AppFooter";
import { RootsMark } from "@/components/RootsMark";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const DEMO = [
  { label: "Admin", email: "admin@roots.sg" },
  { label: "Contract Mgr", email: "manager@roots.sg" },
  { label: "Supervisor", email: "supervisor@roots.sg" },
  { label: "Field Crew", email: "crew@roots.sg" },
  { label: "Client", email: "client@roots.sg" },
  { label: "Auditor", email: "auditor@roots.sg" },
];

const ACRONYM = [
  ["R", "Resilience"],
  ["O", "Optimise"],
  ["O", "Outcomes"],
  ["T", "Technology"],
  ["S", "Sustainable Business"],
];

const STANDARDS_LINE =
  "NParks LEAF · LIAS Outcome-based Contracting · NParks LSTP · URA LUSH · PUB ABC Waters · BCA Green Mark";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      setError(apiErr(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
    <div className="grid flex-1 lg:grid-cols-2">
      {/* visual side */}
      <div
        className="relative hidden flex-col justify-between overflow-hidden bg-forest-deep p-10 text-emerald-50 lg:flex xl:p-12"
        style={{
          backgroundImage:
            "linear-gradient(to bottom, rgba(22,41,32,0.72), rgba(22,41,32,0.94)), url('https://images.unsplash.com/photo-1558289675-f8a783516e7b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sage-accent">
            <RootsMark className="h-6 w-6 text-white" />
          </div>
          <div className="leading-tight">
            <span className="font-heading text-2xl font-bold">ROOTS</span>
            <p className="-mt-0.5 text-[10px] font-medium uppercase tracking-[0.22em] text-emerald-200/70">Framework by SusTek</p>
          </div>
        </div>

        <div className="max-w-md">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-sage-light/90">
            An experimental performance &amp; evidence framework for sustainable landscaping
          </p>
          <h1 className="mt-3 font-heading text-3xl font-bold leading-[1.15] xl:text-4xl">
            Prove landscape performance — aligned to Singapore&apos;s green standards.
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-emerald-100/80">
            ROOTS turns everyday field work into deterministic performance scores and audit-ready evidence —
            mapped to the national frameworks your clients already report against.
          </p>

          {/* What ROOTS stands for */}
          <div className="mt-6 space-y-2" data-testid={LOGIN.acronym}>
            {ACRONYM.map(([l, w], i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-sage-accent font-heading text-xs font-bold text-white">{l}</span>
                <span className="font-medium text-emerald-50/95">{w}</span>
              </div>
            ))}
          </div>

          {/* Capture once. Prove it everywhere. */}
          <div className="mt-6 flex items-start gap-2.5 rounded-xl border border-emerald-300/15 bg-emerald-950/40 p-3.5">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-sage-light" />
            <div>
              <p className="font-heading text-sm font-bold">Capture once. Prove it everywhere.</p>
              <p className="mt-0.5 text-xs leading-relaxed text-emerald-100/70">
                One field capture flows into metrics, ROOTS scores, standards readiness and a traceable monthly client evidence pack.
              </p>
            </div>
          </div>
        </div>

        <p className="text-[11px] leading-relaxed text-emerald-200/55">Aligned to {STANDARDS_LINE} · Lestari-ready data model</p>
      </div>

      {/* form side */}
      <div className="flex items-center justify-center bg-canvas p-6">
        <div className="w-full max-w-sm animate-fade-up">
          {/* mobile brand + compact intro */}
          <div className="mb-6 lg:hidden">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-forest-mid">
                <RootsMark className="h-5 w-5 text-white" />
              </div>
              <div className="leading-tight">
                <span className="font-heading text-xl font-bold text-forest-dark">ROOTS</span>
                <p className="-mt-0.5 text-[9px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Framework by SusTek</p>
              </div>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              An experimental performance &amp; evidence framework for sustainable landscaping — field work becomes deterministic scores and
              audit-ready evidence aligned to NParks LEAF, LIAS OBC, LSTP, URA LUSH, PUB ABC Waters &amp; BCA Green Mark.
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid={LOGIN.acronym}>
              {ACRONYM.map(([l, w], i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full border border-sage-light bg-sage-surface px-2 py-0.5 text-[11px] text-forest-dark">
                  <b className="text-forest-mid">{l}</b> {w}
                </span>
              ))}
            </div>
          </div>

          <h2 className="font-heading text-2xl font-bold text-forest-dark">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">Access your landscape operations console.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.sg" required className="mt-1" data-testid={LOGIN.emailInput} />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required className="mt-1" data-testid={LOGIN.passwordInput} />
            </div>
            {error && <p className="text-sm font-medium text-destructive">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-forest-mid hover:bg-forest-dark" data-testid={LOGIN.submitButton}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
            </Button>
          </form>

          <div className="mt-6 rounded-xl border border-sage-light bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Try a demo role</p>
            <p className="mb-2 text-[11px] text-muted-foreground">Pick a role to fill its email, then enter your password to sign in.</p>
            <div className="flex flex-wrap gap-1.5">
              {DEMO.map((d) => (
                <button key={d.email} type="button"
                  onClick={() => setEmail(d.email)}
                  data-testid={`demo-role-${d.email.split("@")[0]}`}
                  className="rounded-full border border-sage-light bg-sage-surface px-2.5 py-1 text-xs font-medium text-forest-dark hover:bg-sage-light">
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Just exploring?{" "}
            <Link to="/liasroots" className="font-semibold text-forest-mid hover:underline" data-testid={LOGIN.enquiryLink}>
              Register your interest
            </Link>
          </p>
        </div>
      </div>
    </div>
    <AppFooter />
    </div>
  );
}
